/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import * as queryString from "query-string";
import { useNavigate, useLocation, NavigateOptions } from "react-router-dom";
import { getTypedKeys, StrNumBool } from "~/tools";

type SetValue = number | string | undefined;

type QueryState = {
  page?: number;
  limit?: number;
  search?: string;
  query?: string;
  customerId?: string;
};

type QueryStateKey = keyof QueryState;

function useUpdateUrl() {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const updateURL = (
    queryState: Record<string, unknown>,
    navigateOptions?: NavigateOptions
  ) => {
    navigate(
      pathname +
        "?" +
        queryString.stringify(queryState, {
          arrayFormat: "index",
        }),
      navigateOptions
    );
  };

  return { updateURL };
}

export function useQueryState<
  T extends queryString.ParsedQuery<StrNumBool | undefined>,
>() {
  const { search } = useLocation();

  const queryState = React.useMemo(() => {
    const parsed = queryString.parse(search, {
      arrayFormat: "index",
      parseBooleans: true,
      parseNumbers: true,
    }) as QueryState & T;

    return {
      ...parsed,
      // Preserve leading zeros, e.g. 00232434 -> 00232434, `parseNumbers: true` will strip leading zeroes, in some cases that is not desired
      ...(parsed.account != null && { account: extractAccount(search) }),
      ...(parsed.query != null && { query: extractQuery(search) }),
    };
  }, [search]);

  const { updateURL } = useUpdateUrl();

  const set = (field: QueryStateKey | keyof T) => (value: SetValue) => {
    updateURL({ ...queryState, [field]: value });
  };

  const updateQueryState = (fields: QueryState & T) => {
    updateURL({ ...queryState, ...fields });
  };

  const replaceQueryState = (fields: QueryState & T) => {
    updateURL({ ...fields });
  };

  const removeFields = (
    fields: (QueryStateKey | keyof T)[],
    navigateOptions?: NavigateOptions
  ) => {
    const fieldsToKeep = getTypedKeys(queryState).filter(
      (key) => !fields.includes(key)
    );
    const savedQueryState = fieldsToKeep.reduce((acc, curr) => {
      return {
        ...acc,
        [curr]: queryState[curr],
      };
    }, {});

    updateURL(savedQueryState, navigateOptions);
  };

  return {
    state: {
      ...queryState,
      limit: queryState.limit ?? 20,
      page: queryState.page ?? 0,
    },
    set,
    updateQueryState,
    setQueryState: replaceQueryState,
    removeFields,
  };
}

export function useQueryStateWithTabs<T extends { tab?: number }>() {
  const { state, set, updateQueryState, setQueryState, removeFields } =
    useQueryState<T>();

  return {
    state: {
      ...state,
      limit: state.limit ?? 20,
      page: state.page ?? 0,
      tab: state.tab ?? 0,
    },
    set,
    updateQueryState,
    setQueryState,
    removeFields,
  };
}

export function useFilteredQueryState<
  T extends { tab?: string; account?: string[] },
>(whiteList: string[]) {
  const { state, set, updateQueryState, setQueryState, removeFields } =
    useQueryState<T>();

  const filteredState = Object.entries(state)
    .filter(([key]) => whiteList.includes(key))
    .reduce<typeof state>((acc, [key, value]) => {
      return {
        ...acc,
        [key]: value,
      };
      // @ts-expect-error ignored temporary after migration to TypeScript 4.9
    }, {});

  return {
    state: filteredState,
    set,
    updateQueryState,
    setQueryState,
    removeFields,
  };
}

export function useQueryStateWithFilters<T>(filters: T) {
  const { state, updateQueryState, setQueryState, removeFields } =
    useQueryState();

  type TKey = Extract<keyof T, string>;
  // @ts-expect-error ignored temporary after migration to TypeScript 4.9
  const filterEntries = Object.entries(filters);
  // @ts-expect-error ignored temporary after migration to TypeScript 4.9
  const activeFilters: QueryStateFilter<T>[] = filterEntries
    // @ts-expect-error ignored temporary after migration to TypeScript 4.9
    .filter(([key, value]) => isValidFilter<T>([key as TKey, value]))
    .map(([key, value]) => [key as TKey, value]);

  return {
    activeFilters,
    removeFields,
    clearFilters: () => {
      const { limit, tab, query } = state;
      // @ts-expect-error ignored temporary after migration to TypeScript 4.9
      setQueryState({ page: "0", limit, tab, query });
    },
    // @ts-expect-error ignored temporary after migration to TypeScript 4.9
    setFilters: (newFilters: T) => updateQueryState(newFilters),
  };
}

const extractAccount = (search: string) => {
  return queryString.parse(search, {
    arrayFormat: "index",
    parseBooleans: true,
    parseNumbers: false,
  }).account;
};

const extractQuery = (search: string) => {
  return queryString.parseUrl(search ?? "", {
    parseNumbers: false,
  }).query.query;
};

type ValidFilter<T> = [Extract<keyof T, string>, string];
export type QueryStateFilterValue = StrNumBool | StrNumBool[] | null;
export type QueryStateFilter<T> = [
  Extract<keyof T, string>,
  QueryStateFilterValue,
];

function isValidFilter<T>(entry: QueryStateFilter<T>): entry is ValidFilter<T> {
  return entry[1] != null && entry[1] !== "";
}
